'use client'

import { Check, ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Button } from './button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu'

interface SimpleSelectOption {
  value: string
  label: string
}

interface SimpleSelectProps {
  value: string
  onChange: (value: string) => void
  options: SimpleSelectOption[]
  placeholder?: string
  width?: 'auto' | 'sm' | 'md' | 'lg' | 'xl' | 'full'
  className?: string
  disabled?: boolean
}

const widthClasses = {
  auto: 'min-w-28',
  sm: 'w-32',
  md: 'w-48',
  lg: 'w-64',
  xl: 'w-80',
  full: 'w-full',
}

/**
 * A simple dropdown select component without auto-save functionality.
 * Use this for local state management where you don't need to persist immediately.
 */
const SimpleSelect = ({
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  width = 'md',
  className,
  disabled = false,
}: SimpleSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const selectedOption = options.find((opt) => opt.value === value)

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={isOpen}
          disabled={disabled}
          className={cn('justify-between', widthClasses[width], className)}
        >
          <span className="truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn('z-[9999]', widthClasses[width])}
        align="start"
      >
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onSelect={() => {
              onChange(option.value)
              setIsOpen(false)
            }}
            className="cursor-pointer"
          >
            <div className="flex w-full items-center justify-between">
              <span>{option.label}</span>
              {value === option.value && (
                <Check className="h-4 w-4 text-green-500" />
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { SimpleSelect }
export type { SimpleSelectOption, SimpleSelectProps }
